import React, { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
  Alert,
  useTheme,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  ArrowBack as ArrowBackIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PivotTableUI from 'react-pivottable';
import 'react-pivottable/pivottable.css';
import * as XLSX from 'xlsx';
import ventasInformesService, { VentasRpt01Row } from '../../services/ventasInformesService';

function rowToPivotRecord(row: VentasRpt01Row): Record<string, unknown> {
  return { ...row };
}

const WEEK_OPTS = { weekStartsOn: 1 as const };

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

/** Períodos rápidos de fechas (alineado a ventas-por-producto / libro-mayor). */
type QuickChipVentas =
  | 'hoy'
  | 'semana'
  | 'mes'
  | 'mesPasado'
  | 'tresMeses'
  | 'seisMeses'
  | 'anioActual';

const QUICK_CHIP_VENTAS_KEYS: QuickChipVentas[] = [
  'hoy',
  'semana',
  'mes',
  'mesPasado',
  'tresMeses',
  'seisMeses',
  'anioActual',
];

function computeQuickRangeVentas(which: QuickChipVentas): { desde: string; hasta: string } {
  const now = new Date();
  switch (which) {
    case 'hoy': {
      const d = toIsoDate(now);
      return { desde: d, hasta: d };
    }
    case 'semana':
      return {
        desde: toIsoDate(startOfWeek(now, WEEK_OPTS)),
        hasta: toIsoDate(endOfWeek(now, WEEK_OPTS)),
      };
    case 'mes':
      return {
        desde: toIsoDate(startOfMonth(now)),
        hasta: toIsoDate(endOfMonth(now)),
      };
    case 'mesPasado': {
      const ref = subMonths(now, 1);
      return {
        desde: toIsoDate(startOfMonth(ref)),
        hasta: toIsoDate(endOfMonth(ref)),
      };
    }
    case 'tresMeses':
      return {
        desde: toIsoDate(startOfDay(subMonths(now, 3))),
        hasta: toIsoDate(endOfDay(now)),
      };
    case 'seisMeses':
      return {
        desde: toIsoDate(startOfDay(subMonths(now, 6))),
        hasta: toIsoDate(endOfDay(now)),
      };
    case 'anioActual':
      return {
        desde: toIsoDate(startOfYear(now)),
        hasta: toIsoDate(endOfYear(now)),
      };
    default: {
      const _exhaustive: never = which;
      return _exhaustive;
    }
  }
}

function findMatchingQuickChipVentas(desde: string, hasta: string): QuickChipVentas | null {
  for (const key of QUICK_CHIP_VENTAS_KEYS) {
    const r = computeQuickRangeVentas(key);
    if (desde === r.desde && hasta === r.hasta) return key;
  }
  return null;
}

const InformeVentasDinamico: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [fechaDesde, setFechaDesde] = useState(() => computeQuickRangeVentas('mes').desde);
  const [fechaHasta, setFechaHasta] = useState(() => computeQuickRangeVentas('mes').hasta);
  const [nroCliente, setNroCliente] = useState('');
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [nroTipoFormulario, setNroTipoFormulario] = useState('');
  const [nroSuc, setNroSuc] = useState('');
  const [maxRows, setMaxRows] = useState('5000');

  const [rows, setRows] = useState<VentasRpt01Row[]>([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pivotState, setPivotState] = useState<Record<string, unknown>>({});
  const [deferUpdates, setDeferUpdates] = useState(true);

  const pivotData = useMemo(() => rows.map(rowToPivotRecord), [rows]);

  const presetActivo = useMemo(
    () => findMatchingQuickChipVentas(fechaDesde, fechaHasta),
    [fechaDesde, fechaHasta]
  );
  const periodoPersonalizado = Boolean(fechaDesde && fechaHasta && presetActivo === null);

  const resumenFiltrosAvanzados = useMemo(() => {
    const partes: string[] = [];
    if (nroCliente.trim()) partes.push('Cliente');
    if (codigoArticulo.trim()) partes.push('Artículo');
    if (nroTipoFormulario.trim()) partes.push('Tipo formulario');
    if (nroSuc.trim()) partes.push('Sucursal');
    if (maxRows !== '5000') partes.push('Máx. filas');
    if (periodoPersonalizado) partes.push('Fechas personalizadas');
    return partes.length > 0 ? partes.join(' · ') : '';
  }, [
    nroCliente,
    codigoArticulo,
    nroTipoFormulario,
    nroSuc,
    maxRows,
    periodoPersonalizado,
  ]);

  const applyQuickChip = useCallback((which: QuickChipVentas) => {
    const r = computeQuickRangeVentas(which);
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
  }, []);

  const handleCargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y hasta.', { variant: 'warning' });
      return;
    }
    const max = parseInt(maxRows, 10);
    if (Number.isNaN(max) || max < 1) {
      enqueueSnackbar('Máx. filas inválido.', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const res = await ventasInformesService.getRptVentas01({
        fechaDesde,
        fechaHasta,
        nroCliente: nroCliente.trim() ? parseInt(nroCliente, 10) : undefined,
        codigoArticulo: codigoArticulo.trim() || undefined,
        nroTipoFormulario: nroTipoFormulario.trim() || undefined,
        nroSuc: nroSuc.trim() ? parseInt(nroSuc, 10) : undefined,
        maxRows: Math.min(max, 15000),
      });

      setRows(res.data);
      setTotalMatched(res.totalMatched);
      setTruncated(res.truncated);

      setPivotState({
        rows: ['nombre'],
        cols: ['tipDescripcion'],
        vals: ['subtotalFinal'],
        aggregatorName: 'Sum',
        rendererName: 'Table',
      });

      if (res.truncated) {
        enqueueSnackbar(
          `Se cargaron ${res.data.length} filas de ${res.totalMatched} coincidentes. Ajustá filtros o el máximo de filas.`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(`${res.data.length} filas cargadas.`, { variant: 'success' });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudieron cargar los datos del informe.', { variant: 'error' });
      setRows([]);
      setTotalMatched(0);
      setTruncated(false);
      setPivotState({});
    } finally {
      setLoading(false);
    }
  }, [
    fechaDesde,
    fechaHasta,
    nroCliente,
    codigoArticulo,
    nroTipoFormulario,
    nroSuc,
    maxRows,
    enqueueSnackbar,
  ]);

  const handleExportXlsx = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows as unknown as Record<string, unknown>[]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
    const name = `informe-ventas-dinamico-${fechaDesde}_${fechaHasta}.xlsx`;
    XLSX.writeFile(wb, name);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/ventas/informes')}
                  sx={{ textTransform: 'none' }}
                >
                  Informes
                </Button>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Informe dinámico de ventas
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                Arrastrá campos a Filas, Columnas y Filtros, y elegí valores a agregar.
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{ justifyContent: { xs: 'flex-end', sm: 'flex-end' }, flexShrink: 0 }}
            >
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                onClick={() => void handleCargar()}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Cargar datos
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportXlsx}
                disabled={rows.length === 0}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Exportar Excel (dataset)
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
                Período rápido
              </Typography>
              {(
                [
                  { key: 'hoy' as const, label: 'Hoy' },
                  { key: 'semana' as const, label: 'Semana actual' },
                  { key: 'mes' as const, label: 'Mes actual' },
                  { key: 'mesPasado' as const, label: 'Mes pasado' },
                  { key: 'tresMeses' as const, label: 'Últ. 3 meses' },
                  { key: 'seisMeses' as const, label: 'Últ. 6 meses' },
                  { key: 'anioActual' as const, label: 'Año actual' },
                ] as const
              ).map(({ key, label }) => (
                <Chip
                  key={key}
                  icon={
                    <CalendarMonthIcon
                      sx={{
                        fontSize: 18,
                        color: presetActivo === key ? 'inherit' : 'primary.main',
                      }}
                    />
                  }
                  label={label}
                  size="small"
                  onClick={() => applyQuickChip(key)}
                  color={presetActivo === key ? 'primary' : 'default'}
                  variant={presetActivo === key ? 'filled' : 'outlined'}
                />
              ))}
              <Chip
                label="Personalizado"
                size="small"
                icon={
                  <CalendarMonthIcon
                    sx={{
                      fontSize: 18,
                      color: periodoPersonalizado ? 'inherit' : 'primary.main',
                    }}
                  />
                }
                color={periodoPersonalizado ? 'primary' : 'default'}
                variant={periodoPersonalizado ? 'filled' : 'outlined'}
                title="Las fechas no coinciden con un período rápido; podés editarlas en Fecha Desde / Hasta."
                sx={{ cursor: 'default' }}
              />
            </Stack>

            <Accordion
              variant="outlined"
              disableGutters
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, pr: 1 }}>
                  <FilterAltIcon color="primary" fontSize="small" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Filtros avanzados
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {resumenFiltrosAvanzados}
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Fecha desde"
                      name="fechaDesde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Fecha hasta"
                      name="fechaHasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="Nº cliente"
                      value={nroCliente}
                      onChange={(e) => setNroCliente(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Opcional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="Código artículo"
                      value={codigoArticulo}
                      onChange={(e) => setCodigoArticulo(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Contiene"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="Tipo formulario"
                      value={nroTipoFormulario}
                      onChange={(e) => setNroTipoFormulario(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Opcional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="Nº sucursal"
                      value={nroSuc}
                      onChange={(e) => setNroSuc(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Opcional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="Máx. filas"
                      value={maxRows}
                      onChange={(e) => setMaxRows(e.target.value)}
                      fullWidth
                      size="small"
                      helperText="Máx. 15000"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deferUpdates}
                          onChange={(e) => setDeferUpdates(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Diferir actualización del pivot (mejor con muchas filas)"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          {truncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Resultado truncado: hay {totalMatched} filas que coinciden; se muestran {rows.length}. Reducí el rango o
              aumentá el máximo (hasta 15000).
            </Alert>
          )}

          {!truncated && rows.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {rows.length} fila(s) cargada(s)
              {totalMatched !== rows.length ? ` · ${totalMatched} coincidencias en servidor` : ''}
            </Typography>
          )}

          {loading && rows.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              Configurá filtros y pulsá «Cargar datos».
            </Typography>
          ) : (
            <Box
              sx={{
                '& .pvtUi': {
                  color: 'text.primary',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.875rem',
                  minWidth: { xs: 640, md: 'auto' },
                },
                '& .pvtAxisContainer, & .pvtVals': {
                  backgroundColor: '#fafafa',
                },
                '& table.pvtTable': {
                  fontSize: '0.8125rem',
                },
                overflowX: 'auto',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                maxHeight: { xs: '60vh', md: '70vh' },
                maxWidth: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <PivotTableUI
                data={pivotData}
                onChange={(s) => {
                  const next = { ...(s as Record<string, unknown>) };
                  delete next.data;
                  setPivotState(next);
                }}
                deferUpdates={deferUpdates}
                {...pivotState}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InformeVentasDinamico;
